import * as Types from '@aeppic/types'
import { DocumentIdIterator, IIndex } from './index-interface'
import { resolveField, IndexOptions } from './document-index'

export class MapIndex implements IIndex {
  private _valuesToDocumentIds: Map<any, Set<Types.DocumentId>> = new Map()
  private _documentIdsToValues: Map<Types.DocumentId, any[]> = new Map()
  private _isSuperSet = false
  private _prefixLength: number | null = null

  constructor(
    public fieldPath: string,
    private _options: IndexOptions
  ) {
    if ('type' in _options && _options.type === 'prefix') {
      this._isSuperSet = true
      this._prefixLength = _options.length
    }
  }

  get prefixLength() { return this._prefixLength }
  get isSuperSet() { return this._isSuperSet }

  size(): number { return this._valuesToDocumentIds.size }
  entryCount(): number { return this._documentIdsToValues.size }

  mappedValues(): IterableIterator<any> {
    return this._valuesToDocumentIds.keys()
  }

  addIfMatches(entry: Types.Document): void {
    if (this._options.inclusionFilter && !this._options.inclusionFilter(entry)) return

    const fieldValue = resolveField(entry, this.fieldPath)
    if (fieldValue != null && (typeof fieldValue === 'string' ? fieldValue !== '' : true)) {
      this._add(fieldValue, entry)
    }
  }

  private _add(value: any, entry: Types.Document): void {
    const mappedValues = Array.isArray(value) ? value.map(v => this._mapValue(v)) : [this._mapValue(value)]
    for (const mappedValue of mappedValues) {
      this._addSingleValue(mappedValue, entry)
    }
    this._documentIdsToValues.set(entry.id, mappedValues)
  }

  private _addSingleValue(value: any, entry: Types.Document): void {
    if (value == null) return
    const list = this._getEntryList(value)
    list.add(entry.id)
  }

  has(id: string): boolean { return this._documentIdsToValues.has(id) }

  remove(id: string): void {
    const values = this._documentIdsToValues.get(id)
    this._documentIdsToValues.delete(id)
    if (values) {
      for (const singleMappedValue of values) {
        this._removeSingleValue(singleMappedValue, id)
      }
    }
  }

  private _removeSingleValue(singleValue: any, entryId: string): void {
    const directEntries = this._valuesToDocumentIds.get(singleValue)
    if (directEntries) {
      directEntries.delete(entryId)
      if (directEntries.size === 0) {
        this._valuesToDocumentIds.delete(singleValue)
      }
    }
  }

  getDocumentIdsForValue(value: any): DocumentIdIterator {
    if (Array.isArray(value)) {
      throw new Error('MapIndex.getDocumentIdsForValue should never receive array values - arrays must be unpacked by iterator-builders')
    }

    const mappedValue = this._mapValue(value)
    if (mappedValue == null) return new DocumentIdIterator(new Set())
    const entries = this._valuesToDocumentIds.get(mappedValue)
    return new DocumentIdIterator(entries || new Set())
  }

  getEntriesForRange(minValue: any, maxValue: any): Array<[key: any, entries: DocumentIdIterator]> {
    const mappedMin = this._mapValue(minValue)
    const mappedMax = this._mapValue(maxValue)
    const matchingEntries: Array<[key: any, entries: DocumentIdIterator]> = []

    if (mappedMin != null && mappedMin === mappedMax) {
      const documentIds = this._valuesToDocumentIds.get(mappedMin)
      return documentIds?.size > 0 ? [[mappedMin, new DocumentIdIterator(documentIds)]] : []
    }

    const sortedValues = Array.from(this._valuesToDocumentIds.keys()).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))

    if (sortedValues.length > 10_000) {
      console.warn('MapIndex: Scanning a large number of values for range query:', sortedValues.length, 'Consider using a BTree index for field', this.fieldPath)
    }

    for (const val of sortedValues) {
      if (val >= mappedMin && val <= mappedMax) {
        const documentIds = this._valuesToDocumentIds.get(val)
        if (documentIds && documentIds.size > 0) {
          matchingEntries.push([val, new DocumentIdIterator(documentIds)])
        }
      } else if (val > mappedMax) break
    }
    return matchingEntries
  }

  private _getEntryList(value: any): Set<Types.DocumentId> {
    let entries = this._valuesToDocumentIds.get(value)
    if (!entries) {
      entries = new Set<Types.DocumentId>()
      this._valuesToDocumentIds.set(value, entries)
    }
    return entries
  }

  private _mapValue(value: any): any {
    if (value == null) return null
    if (typeof value === 'number') return value
    if (typeof value !== 'string') value = value.toString()
    if ('type' in this._options && this._options.type === 'prefix') {
      return value.length > this._options.length ? value.substr(0, this._options.length).toLowerCase() : value.toLowerCase()
    }
    return value
  }
}
